import { Link } from 'react-router-dom';
import { games, categories } from '@/mocks/games';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

function GameCard({ game }: { game: typeof games[0] }) {
  return (
    <Link
      to={`/article/${game.slug}`}
      className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={game.image}
          alt={game.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50/90 text-xs font-medium text-primary-600 whitespace-nowrap">
            <i className="ri-star-fill text-amber-400 text-xs"></i>
            {game.rating}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <span className="inline-flex px-2.5 py-1 rounded-full bg-background-50/90 text-xs font-medium text-foreground-600 whitespace-nowrap">
            {game.difficulty}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-heading text-lg text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors">
          {game.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
          {game.description}
        </p>
        <div className="flex items-center gap-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-line"></i>
            </div>
            {game.players}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line"></i>
            </div>
            {game.time}
          </span>
        </div>
      </div>
    </Link>
  );
}

function ArticleCard({ article }: { article: typeof articles[0] }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="w-full sm:w-48 h-36 rounded-lg overflow-hidden shrink-0">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex flex-col justify-between flex-1 min-w-0">
        <div>
          <h3 className="font-heading text-lg text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
            {article.excerpt}
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-foreground-500 mt-3 flex-wrap">
          <span className="whitespace-nowrap">{article.author}</span>
          <span className="whitespace-nowrap">{article.date}</span>
          <span className="whitespace-nowrap">{article.readTime}阅读</span>
          <div className="flex gap-1.5 flex-wrap">
            {article.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const featuredGames = games.filter((g) => g.featured);
  const featuredArticles = articles.filter((a) => a.featured);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteInfo.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />

      <header className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Vibrant%20playful%20game%20party%20scene%20with%20colorful%20abstract%20shapes%20and%20soft%20gradients%20in%20warm%20orange%20and%20coral%20tones%2C%20minimalist%20modern%20design%2C%20clean%20composition%2C%20bright%20atmosphere%2C%20energetic%20and%20fun%20vibe%2C%20no%20text%2C%20soft%20lighting&width=1800&height=800&seq=hero-main-bg&orientation=landscape"
          alt={siteInfo.name}
          className="absolute inset-0 w-full h-full object-cover object-top"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="w-full max-w-3xl">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 mb-4 leading-tight">
              {siteInfo.name}
            </h1>
            <p className="text-base md:text-lg text-background-50/80 mb-8 max-w-xl mx-auto leading-relaxed">
              {siteInfo.tagline} — 融合趣味惩罚机制与欢乐互动玩法，汇集玩法介绍、规则说明、技巧攻略及同类推荐
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/category/hudong-party"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                探索游戏
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50/20 text-background-50 font-medium text-sm hover:bg-background-50/30 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                  游戏分类
                </h2>
                <p className="text-sm text-foreground-500 mt-2">选择你感兴趣的游戏类型</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-3 p-4 md:p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer group"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <span className="font-heading text-sm text-foreground-800 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                  热门游戏
                </h2>
                <p className="text-sm text-foreground-500 mt-2">最受欢迎的打屁股游戏推荐</p>
              </div>
              <Link
                to="/category/hudong-party"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredGames.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-accent-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-3">
              不知道怎么玩？
            </h2>
            <p className="text-background-50/80 mb-8 max-w-lg mx-auto">
              看看我们的新手攻略，从零开始快速上手打屁股游戏
            </p>
            <Link
              to="/article/beginner-guide"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-background-50 text-accent-600 font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看新手攻略
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line"></i>
              </div>
            </Link>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                  精选攻略
                </h2>
                <p className="text-sm text-foreground-500 mt-2">最实用的游戏技巧与玩法指南</p>
              </div>
              <Link
                to="/category/hudong-party"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                更多文章
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="flex flex-col gap-4">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8 text-center">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于打屁股游戏的常见疑问解答</p>
            </div>
            <div className="max-w-2xl mx-auto space-y-3">
              {[
                { q: '打屁股游戏安全吗？', a: '打屁股游戏以娱乐为目的，建议使用手掌轻拍，力度适中。游戏前应设定好规则边界，确保所有参与者感到舒适和安全。' },
                { q: '打屁股游戏适合哪些场合？', a: '适合朋友聚会、团建活动、家庭娱乐、情侣互动等多种场合。不同场合可以选择不同强度的游戏玩法。' },
                { q: '打屁股游戏需要什么道具？', a: '大部分游戏不需要特殊道具，猜拳、骰子就可以开始。部分游戏可能需要扑克牌、手机等简单道具。' },
                { q: '打屁股游戏可以多少人一起玩？', a: '视具体游戏而定，有的适合2人，有的可以支持10人以上。建议根据场地和参与人数选择合适的游戏。' },
              ].map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="font-medium text-foreground-800 pr-4">{faq.q}</span>
                    <div className="w-6 h-6 flex items-center justify-center shrink-0">
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}